import React, { useState } from 'react';
import './ElementOrderControls.css';

/**
 * 🎯 ElementOrderControls - Drag & Drop Element-Reihenfolge-Steuerung
 * 
 * Ermöglicht es Benutzern, die Reihenfolge von Widget-Elementen per Drag & Drop zu ändern.
 * Funktioniert perfekt im Edit-Modus ohne Konflikte mit Widget Drag & Drop.
 */
function ElementOrderControls({ elementOrder, onOrderChange, editData }) {
    
    const [draggedItem, setDraggedItem] = useState(null);
    const [dropPosition, setDropPosition] = useState(null); // { elementKey, position: 'before'|'after' }
    const [isDragging, setIsDragging] = useState(false);
    
    // Element-Definitionen mit Icons und Labels
    const elementDefinitions = {
        image: {
            label: 'Image',
            icon: '🖼️',
            visible: editData.showContentImage !== false && editData.optionalImage,
            description: 'Content image'
        },
        categoryText: {
            label: 'Category Text',
            icon: '🏷️',
            visible: editData.showCategoryText !== false && editData.categoryText,
            description: 'Small category/tag text'
        },
        richText: {
            label: 'Header Text',
            icon: '📝',
            visible: editData.showRichText !== false && editData.richText,
            description: 'Main headline text'
        },
        simpleText: {
            label: 'Description Text',
            icon: '📄',
            visible: editData.showSimpleText !== false && editData.simpleText,
            description: 'Paragraph description'
        },
        button: {
            label: 'Button',
            icon: '🔘',
            visible: editData.showButtonText !== false && editData.buttonText,
            description: 'Call-to-action button'
        }
    };

    // Sortiere Elemente nach aktueller Reihenfolge
    const sortedElements = Object.entries(elementOrder)
        .sort(([, a], [, b]) => a - b)
        .map(([key]) => key)
        .filter(key => elementDefinitions[key]?.visible);

    // 🎯 OPTIMIZED DRAG & DROP HANDLERS
    const handleDragStart = (e, elementKey) => {
        setDraggedItem(elementKey);
        setIsDragging(true);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', elementKey);
        
        // Optimierter visueller Feedback - weniger DOM-Manipulation
        requestAnimationFrame(() => {
            e.target.style.opacity = '0.5';
        });
    };

    const handleDragEnd = (e) => {
        // Cleanup in einem Frame
        requestAnimationFrame(() => {
            e.target.style.opacity = '1';
            setDraggedItem(null);
            setDropPosition(null);
            setIsDragging(false);
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        // Throttle die Position-Updates um Ruckeln zu vermeiden
        if (!isDragging) return;
        
        const elementKey = e.currentTarget.dataset.elementKey;
        if (draggedItem !== elementKey) {
            const rect = e.currentTarget.getBoundingClientRect();
            const midPoint = rect.top + rect.height / 2;
            const position = e.clientY < midPoint ? 'before' : 'after';
            
            // Nur updaten wenn sich Position geändert hat
            setDropPosition(prev => {
                if (prev?.elementKey !== elementKey || prev?.position !== position) {
                    return { elementKey, position };
                }
                return prev;
            });
        }
    };

    const handleDragEnter = (e, elementKey) => {
        e.preventDefault();
        // Entfernt - Logic ist jetzt in handleDragOver für bessere Performance
    };

    const handleDragLeave = (e) => {
        // Vereinfacht - weniger DOM-Queries
        const rect = e.currentTarget.getBoundingClientRect();
        const isOutside = e.clientX < rect.left || e.clientX > rect.right || 
                         e.clientY < rect.top || e.clientY > rect.bottom;
        
        if (isOutside) {
            setDropPosition(null);
        }
    };

    const handleDrop = (e, targetElementKey) => {
        e.preventDefault();
        
        if (!draggedItem || !dropPosition) {
            return;
        }

        const draggedIndex = sortedElements.indexOf(draggedItem);
        const targetIndex = sortedElements.indexOf(targetElementKey);
        
        // Bestimme die finale Position basierend auf dropPosition
        let finalIndex = targetIndex;
        if (dropPosition.position === 'after') {
            finalIndex = targetIndex + 1;
        }
        
        // Adjustiere Index wenn wir nach rechts verschieben
        if (draggedIndex < finalIndex) {
            finalIndex -= 1;
        }
        
        // Erstelle neue Reihenfolge
        const newSortedElements = [...sortedElements];
        newSortedElements.splice(draggedIndex, 1);
        newSortedElements.splice(finalIndex, 0, draggedItem);
        
        // Konvertiere zurück zu Order-Objekt
        const newOrder = {};
        newSortedElements.forEach((elementKey, index) => {
            newOrder[elementKey] = index + 1;
        });
        
        // Behalte Order-Werte für nicht-sichtbare Elemente
        Object.keys(elementOrder).forEach(key => {
            if (!newSortedElements.includes(key)) {
                newOrder[key] = elementOrder[key];
            }
        });
        
        onOrderChange(newOrder);
        setDropPosition(null);
    };


    // Reset zur Standard-Reihenfolge
    const resetOrder = () => {
        const defaultOrder = {
            image: 1,
            categoryText: 2,
            richText: 3,
            simpleText: 4,
            button: 5
        };
        onOrderChange(defaultOrder);
    };

    return (
        <div className="element-order-controls">
            <div className="element-order-list">
                {sortedElements.map((elementKey, index) => {
                    const element = elementDefinitions[elementKey];
                    const showInsertionBefore = dropPosition?.elementKey === elementKey && dropPosition?.position === 'before';
                    const showInsertionAfter = dropPosition?.elementKey === elementKey && dropPosition?.position === 'after';
                    
                    return (
                        <div key={elementKey} className="element-order-wrapper">
                            {/* Insertion Indicator - Before */}
                            {showInsertionBefore && (
                                <div className="insertion-indicator">
                                    <div className="insertion-line"></div>
                                    <div className="insertion-text">Drop here</div>
                                </div>
                            )}
                            
                            <div 
                                className={`element-order-item ${draggedItem === elementKey ? 'dragging' : ''}`}
                                draggable={true}
                                data-element-key={elementKey}
                                onDragStart={(e) => handleDragStart(e, elementKey)}
                                onDragEnd={handleDragEnd}
                                onDragOver={handleDragOver}
                                onDragEnter={(e) => handleDragEnter(e, elementKey)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, elementKey)}
                            >
                                <div className="drag-handle" title="Drag to reorder">
                                    <i className="fa fa-grip-vertical"></i>
                                </div>
                                
                                <div className="element-info">
                                    <span className="element-icon">{element.icon}</span>
                                    <div className="element-details">
                                        <span className="element-label">{element.label}</span>
                                        <span className="element-description">{element.description}</span>
                                    </div>
                                    <span className="element-position">#{index + 1}</span>
                                </div>
                            </div>
                            
                            {/* Insertion Indicator - After */}
                            {showInsertionAfter && (
                                <div className="insertion-indicator">
                                    <div className="insertion-line"></div>
                                    <div className="insertion-text">Drop here</div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            
            {sortedElements.length > 1 && (
                <div className="element-order-actions">
                    <button
                        className="reset-order-button"
                        onClick={resetOrder}
                        title="Reset to default order"
                    >
                        <i className="fa fa-refresh"></i>
                        Reset Order
                    </button>
                </div>
            )}
            
            {sortedElements.length === 0 && (
                <div className="no-elements-message">
                    <p>No visible elements to reorder. Add content to see ordering options.</p>
                </div>
            )}
        </div>
    );
}

export default ElementOrderControls;
